// Only existing, draft-policy task types are offered. The catalogue from /agents
// still decides whether a task and its agent are available to this organization.
export const AGENT_IDS = [
  "CONTENT_AGENT", "INTELLIGENCE_AGENT", "SALES_AGENT",
  "PROPOSALS_AGENT", "MEETINGS_AGENT", "COMMERCE_AGENT",
];

export const TASK_FIELDS = {
  create_content: ["topic", "platform", "format", "count", "objective", "instructions"],
  content_calendar: ["days", "platforms", "theme", "posts_per_week"],
  repurpose_content: ["source_text", "platforms"],
  trend_scan: ["topic", "horizon", "platform", "user_content"],
  competitor_analysis: ["competitors", "user_content", "focus"],
  content_ideas: ["theme", "platform", "count", "user_content"],
  qualify_lead: ["lead_id", "instructions"],
  draft_outreach: ["lead_id", "channel", "objective", "instructions"],
  follow_up_sequence: ["lead_id", "steps", "channel", "instructions"],
  lead_summary: ["lead_id", "focus"],
  ideal_customer_profile: ["segment"],
  draft_proposal: ["opportunity_id", "lead_id", "services", "budget", "notes", "options_count"],
  follow_up_review: ["lead_id", "notes", "focus", "channel"],
  analyze_meeting: ["meeting_id", "transcript", "lead_id", "title"],
  ugc_script: ["brief", "platform", "duration_s", "style", "objective", "offer", "audience", "inputs"],
};

const platforms = ["instagram", "facebook", "linkedin", "tiktok", "google_business", "pinterest", "newsletter", "blog"];
export const FIELDS = {
  topic: { type: "text" }, theme: { type: "text" }, focus: { type: "text" },
  segment: { type: "text" }, title: { type: "text" }, offer: { type: "text" }, audience: { type: "text" },
  lead_id: { type: "uuid" }, opportunity_id: { type: "uuid" }, meeting_id: { type: "uuid" },
  instructions: { type: "textarea" }, source_text: { type: "textarea" },
  transcript: { type: "textarea" }, notes: { type: "textarea" }, brief: { type: "textarea" },
  platforms: { type: "list", options: platforms }, competitors: { type: "list" },
  services: { type: "list" }, user_content: { type: "list" }, inputs: { type: "list" },
  platform: { type: "select", options: platforms },
  format: { type: "select", options: ["post", "carousel", "reel", "story", "video_script", "article", "gbp_post"] },
  objective: { type: "select", options: ["awareness", "leads", "sales", "engagement", "meeting", "discovery", "follow_up", "reactivation", "proposal_follow_up"] },
  channel: { type: "select", options: ["email", "linkedin", "sms", "whatsapp", "phone_script"] },
  horizon: { type: "select", options: ["week", "month", "quarter"] },
  style: { type: "select", options: ["testimonial_style", "tutorial", "before_after", "day_in_the_life", "problem_solution", "unboxing", "faq", "behind_the_scenes"] },
  count: { type: "integer", min: 1, max: 15 },
  days: { type: "integer", min: 3, max: 14 },
  posts_per_week: { type: "integer", min: 2, max: 7 },
  steps: { type: "integer", min: 2, max: 4 },
  options_count: { type: "integer", min: 1, max: 3 },
  duration_s: { type: "integer", min: 10, max: 180 },
  budget: { type: "number", min: 0 },
};

export const REQUIRED = {
  repurpose_content: ["source_text"],
  qualify_lead: ["lead_id"],
  draft_outreach: ["lead_id"],
  follow_up_sequence: ["lead_id"],
  lead_summary: ["lead_id"],
  follow_up_review: ["lead_id"],
  ugc_script: ["brief"],
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Return a field name for an invalid input; only submit typed, non-empty values.
export function prepareTaskParams(task, values) {
  const params = {};
  for (const key of TASK_FIELDS[task] || []) {
    const spec = FIELDS[key];
    const raw = (values[key] || "").trim();
    if (!raw) {
      if (REQUIRED[task]?.includes(key)) return { invalid: key };
      continue;
    }
    if (spec.type === "uuid" && !UUID.test(raw)) return { invalid: key };
    if (spec.type === "list") {
      const items = raw.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean);
      if (spec.options && items.some((item) => !spec.options.includes(item))) return { invalid: key };
      params[key] = items;
    } else if (spec.type === "number" || spec.type === "integer") {
      const value = Number(raw);
      const max = task === "create_content" && key === "count" ? 5 : spec.max;
      if (!Number.isFinite(value) || (spec.type === "integer" && !Number.isInteger(value)) || value < spec.min || (max && value > max)) return { invalid: key };
      params[key] = value;
    } else {
      if (spec.options && !spec.options.includes(raw)) return { invalid: key };
      params[key] = raw;
    }
  }
  // These tasks need an existing record or facts supplied by the user.
  if (task === "analyze_meeting" && !params.meeting_id && !params.transcript) return { invalid: "transcript" };
  if (task === "draft_proposal" && !params.opportunity_id && !params.lead_id && !params.notes) return { invalid: "notes" };
  return { params };
}